import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User, CreateUserRequest, UpdateUserRequest } from '../models/user.model';

export interface UsersResponse {
  data: User[];
  total: number;
  page: number;
  totalPages: number;
}

export interface UserListFilters {
  page?: number;
  limit?: number;
  userSearch?: string;
  companySearch?: string;
  o?: string;
  t?: string;
  t2?: string;
}

export interface UserListItem {
  id: string;
  firstname: string | null;
  lastname: string | null;
  fullname: string | null;
  username: string;
  countryId: string | null;
  countryName: string | null;
  createdAt: string;
  numberOfCompanies: number;
  tipoUsuarioPersona: number | null;
  bAppIos: boolean | null;
  bAppAndroid: boolean | null;
  isActive: number | null;
  npsAskDate: string | null;
  isEmailActivated: boolean | null;
}

export interface UserListResponse {
  data: UserListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface UserListCatalogs {
  orderOptions: { id: string; label: string }[];
  companyTypeOptions: { id: string; label: string }[];
  personTypeOptions: { id: string; label: string }[];
}

export interface UserProfileCompany {
  relationId: string;
  companyId: string;
  companyName: string;
  department: string | null;
  position: string | null;
  identificationType: number | null;
  identificationTypeLabel: string | null;
  userIdentification: string | null;
  isSuspended: boolean;
}

export interface UserProfile {
  id: string;
  firstname: string | null;
  lastname: string | null;
  fullname: string | null;
  username: string;
  identification: string | null;
  cellphone: string | null;
  countryId: string | null;
  countryName: string | null;
  isActive: boolean;
  bAppIos: boolean;
  bAppAndroid: boolean;
  createdAt: string;
  lastAccess: string | null;
  npsScore: number | null;
  npsAskDate: string | null;
  isEmailActivated: boolean;
  emailNotifications: boolean;
  newsletters: boolean;
  numberOfCompanies: number;
  areaRg: number | null;
  passwordUpdatedAt: string | null;
  hasBulkUploads: boolean;
  companies: UserProfileCompany[];
  canEdit: boolean;
}

export interface UserProfileCatalogs {
  countries: { id: string; name: string }[];
  areaOptions: { id: number; label: string }[];
}

export interface UpdateUserProfilePayload {
  nombre?: string;
  apellido?: string;
  identificacion?: string;
  username?: string;
  cellphone?: string;
  passwordUpdatedAt?: string;
  numberOfCompanies?: number;
  countryId?: number;
  areaRg?: number;
  webBulkUploads?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private apiUrl = `${environment.apiUrl}/users`;

  constructor(private http: HttpClient) {}

  getUserList(filters: UserListFilters): Observable<UserListResponse> {
    let params = new HttpParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.http.get<UserListResponse>(`${this.apiUrl}/lista`, { params });
  }

  getUserListCatalogs(): Observable<UserListCatalogs> {
    return this.http.get<UserListCatalogs>(`${this.apiUrl}/list/catalogs`);
  }

  /** usernameMatch: 'contains' (default) | 'startsWith' | 'endsWith' | 'exact' */
  getUsers(params?: {
    page?: number;
    limit?: number;
    userId?: string;
    companyId?: string;
    name?: string;
    email?: string;
    username?: string;
    usernameMatch?: 'contains' | 'startsWith' | 'endsWith' | 'exact';
    usernameContains?: string;
    isActive?: string;
  }): Observable<User[] | UsersResponse> {
    if (params) {
      let httpParams = new HttpParams();
      if (params.page) httpParams = httpParams.set('page', params.page.toString());
      if (params.limit) httpParams = httpParams.set('limit', params.limit.toString());
      if (params.userId) httpParams = httpParams.set('userId', params.userId);
      if (params.companyId) httpParams = httpParams.set('companyId', params.companyId);
      if (params.name) httpParams = httpParams.set('name', params.name);
      if (params.email) httpParams = httpParams.set('email', params.email);
      if (params.username != null) httpParams = httpParams.set('username', params.username);
      if (params.usernameMatch) httpParams = httpParams.set('usernameMatch', params.usernameMatch);
      if (params.usernameContains) httpParams = httpParams.set('usernameContains', params.usernameContains);
      if (params.isActive) httpParams = httpParams.set('isActive', params.isActive);

      return this.http.get<UsersResponse>(this.apiUrl, { params: httpParams });
    }

    return this.http.get<User[]>(this.apiUrl);
  }

  getUser(id: number): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`);
  }

  createUser(user: CreateUserRequest): Observable<User> {
    return this.http.post<User>(this.apiUrl, user);
  }

  updateUser(id: number, user: UpdateUserRequest): Observable<User> {
    return this.http.patch<User>(`${this.apiUrl}/${id}`, user);
  }

  deleteUser(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  assignUserToCompany(userId: number, companyId: number, permissions?: any): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${userId}/assign-company`, {
      companyId,
      permissions
    });
  }

  removeUserFromCompany(userId: number, companyId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${userId}/companies/${companyId}`);
  }

  getUserProfile(id: number): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiUrl}/${id}/profile`);
  }

  getUserProfileCatalogs(): Observable<UserProfileCatalogs> {
    return this.http.get<UserProfileCatalogs>(`${this.apiUrl}/profile/catalogs`);
  }

  updateUserProfile(id: number, payload: UpdateUserProfilePayload): Observable<UserProfile> {
    return this.http.patch<UserProfile>(`${this.apiUrl}/${id}/profile`, payload);
  }

  resetUserPassword(id: number): Observable<{ password: string }> {
    return this.http.post<{ password: string }>(`${this.apiUrl}/${id}/reset-password`, {});
  }

  setUserEmailNotifications(id: number, active: boolean): Observable<UserProfile> {
    return this.http.patch<UserProfile>(`${this.apiUrl}/${id}/email-notifications`, { active });
  }

  setUserNewsletters(id: number, active: boolean): Observable<UserProfile> {
    return this.http.patch<UserProfile>(`${this.apiUrl}/${id}/newsletters`, { active });
  }

  updateUserStatus(id: number, active: boolean): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}/status`, { active });
  }
}
